// H42 — shared collapsible block. The WHOLE title row is the toggle (cursor:pointer,
// role=button, keyboard accessible); a chevron indicates state; the collapsed state is
// persisted in localStorage under `storageKey` so a choice survives reloads. There is NO
// separate collapse button — clicking the title is the only affordance (the Network Graph
// block established this pattern; H42 generalizes it to every card/section).
//
// This component is kept deliberately small and is mirrored in the node dashboard so the
// two dashboards behave identically (like theme-tokens.css / layout.css).

import { useCallback, useState, type KeyboardEvent, type ReactNode } from 'react';

function readCollapsed(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey) return fallback;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null;
    return raw == null ? fallback : raw === '1';
  } catch {
    return fallback;
  }
}

/** Hook form: a boolean + toggle that persists to localStorage. */
export function useCollapsed(
  storageKey?: string,
  defaultCollapsed = false,
): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(storageKey, defaultCollapsed));
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        if (storageKey) localStorage?.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* ignore unavailable / full storage */
      }
      return next;
    });
  }, [storageKey]);
  return [collapsed, toggle];
}

export function CollapsibleSection({
  title,
  actions,
  children,
  storageKey,
  defaultCollapsed = false,
  className = '',
  bodyClassName = '',
  headClassName = '',
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  storageKey?: string;
  defaultCollapsed?: boolean;
  className?: string;
  bodyClassName?: string;
  headClassName?: string;
}) {
  const [collapsed, toggle] = useCollapsed(storageKey, defaultCollapsed);

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <section className={`card collapsible ${collapsed ? 'is-collapsed' : ''} ${className}`}>
      <header
        className={`card-head collapsible-head ${headClassName}`}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span className="collapse-chevron" aria-hidden="true">
          {collapsed ? '▸' : '▾'}
        </span>
        <h2>{title}</h2>
        {actions != null && (
          // Clicks on the actions area must not toggle the section.
          <div className="card-actions" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </header>
      {!collapsed && <div className={`card-body ${bodyClassName}`}>{children}</div>}
    </section>
  );
}
